import { internalLogger } from '../../../interface/v1/clientOsLogger';
import { jWebStates } from '../Initializer';
import type {
  CloseServiceInstanceOptions,
  GetServicesOptions,
  JWebWindow,
  LaunchServiceOptions,
  ServiceInstance,
  ServiceList,
  ServiceRoutingError,
  ServiceRoutingPluginType
} from '../types';
import { loggerDecorator } from '../../../interface/v1/clientOsLogger/decorators';

/**
 * Facade for ServiceRouting plugin operations (Native only)
 */
export class ServiceRoutingFacade {
  /**
   * Get the ServiceRouting plugin
   */
  private static async getServiceRoutingPlugin(): Promise<
    ServiceRoutingPluginType | undefined
  > {
    await jWebStates.JWebLibrary;

    const windowServiceRouting = (window as JWebWindow)?.JWeb?.Plugins
      ?.ServiceRouting;

    if (windowServiceRouting === undefined) {
      internalLogger?.error(
        'Unable to obtain ServiceRouting (missing some JWeb plugin)'
      );
    }
    return windowServiceRouting;
  }

  /**
   * Close the service instance
   * @param data Optional data to pass when closing the service instance
   */
  @loggerDecorator.debug.d3()
  public static closeServiceInstance(data?: CloseServiceInstanceOptions): void {
    const windowServiceRouting = (window as JWebWindow)?.JWeb?.Plugins
      ?.ServiceRouting;

    try {
      windowServiceRouting.closeServiceInstance(data);
    } catch (error) {
      internalLogger?.error(
        'JWeb error: ServiceRouting:closeServiceInstance error',
        error
      );
    }
  }

  /**
   * Get available services
   * @param options Optional parameters for getting services
   * @returns A list of available services or undefined if there was an error
   */
  @loggerDecorator.debug.d3()
  public static async getServices(
    options?: GetServicesOptions
  ): Promise<ServiceList | undefined> {
    const ServiceRouting = await ServiceRoutingFacade.getServiceRoutingPlugin();
    try {
      const serviceList = await ServiceRouting.getServices(options);
      if ('errorType' in serviceList) {
        internalLogger?.error('JWeb error: getServices error', serviceList);
        return undefined;
      }
      return serviceList;
    } catch (error) {
      internalLogger?.error('JWeb error: getServices error', error);
      return undefined;
    }
  }

  /**
   * Get launch options for the currently active service instance
   * @returns Launch options for the current service instance or undefined if there was an error
   */
  @loggerDecorator.debug.d3()
  public static async getServiceInstanceLaunchOptions(): Promise<
    LaunchServiceOptions | undefined
  > {
    const ServiceRouting = await ServiceRoutingFacade.getServiceRoutingPlugin();
    try {
      const launchServiceOptionsResult =
        await ServiceRouting.getServiceInstanceLaunchOptions();
      if ('errorType' in launchServiceOptionsResult) {
        internalLogger?.error(
          'JWeb error: getServiceInstanceLaunchOptions error',
          launchServiceOptionsResult
        );
        return undefined;
      }
      return launchServiceOptionsResult;
    } catch (error) {
      internalLogger?.error(
        'JWeb error: getServiceInstanceLaunchOptions error',
        error
      );
      return undefined;
    }
  }

  /**
   * Launch a service with the provided options
   * @param launchServiceOptions Options for launching the service
   * @returns Information about the launched service instance or undefined if there was an error
   */
  @loggerDecorator.debug.d3()
  public static async launchService(
    launchServiceOptions: LaunchServiceOptions
  ): Promise<ServiceInstance | ServiceRoutingError | undefined> {
    const ServiceRouting = await ServiceRoutingFacade.getServiceRoutingPlugin();

    try {
      const serviceInstance = await ServiceRouting.launchService(
        launchServiceOptions
      );
      return serviceInstance;
    } catch (error) {
      internalLogger?.error('JWeb error: launchService error', error);
      return undefined;
    }
  }
}
